import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MarkerType,
  Node,
  Edge,
  Connection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Badge } from 'react-bootstrap';
import { hasAccess } from 'helpers/access';

import { ALL_INITIAL_NODES } from 'components/service-map/data/initialNodes';
import { INITIAL_EDGES } from 'components/service-map/data/initialEdges';
import { CONNECTOR_STYLES } from 'components/service-map/constants/connectorStyles';
import { ZONE_COLORS, GROUP_COLORS } from 'components/service-map/constants/groupColors';
import { ServiceData, ActorData, ConnectorData, NetworkZone } from 'components/service-map/types/diagram.types';
import { useProject } from 'contexts/ProjectContext';
import ApiService from 'helpers/apiService';

// OSS-FLOW-1 (вариант 1, фронт-only): «Карта потоков данных» в Диаграммах.
// Строится из курируемых данных Карты сервисов (узлы + рёбра INITIAL_EDGES, у
// которых уже есть тип коннектора и направление). Линза understand-anything:
// группировка по зонам (колонки), направление/тип/вес рёбер, точки входа,
// инспектор узла (входящие/исходящие потоки), поиск. Per-project — фильтр по
// активным сервисам (service_connect.service_map_ids). Без бэкенда и LLM.
// Редактируемые/персистируемые рёбра — отдельный блок OSS-FLOW-2 (вариант 2).

// Порядок зон слева-направо (поток: акторы → шлюз → внутренние → kafka → партнёры).
const ZONE_ORDER: NetworkZone[] = ['zone-external', 'zone-dmz', 'zone-internal', 'zone-kafka', 'zone-partners'];
const ZONE_LABEL: Record<NetworkZone, string> = {
  'zone-external': 'Внешние / Клиенты',
  'zone-dmz': 'DMZ / Шлюзы',
  'zone-kafka': 'Kafka',
  'zone-internal': 'Внутренние сервисы',
  'zone-partners': 'Партнёры',
};
const COL_W = 300;
const ROW_H = 76;
const X0 = 40;
const Y0 = 60;

type FlowNode = Node<ServiceData | ActorData>;

const isServiceOrActor = (n: Node) => n.type === 'service' || n.type === 'actor';
const nodeName = (n: Node): string => {
  const d = n.data as Partial<ServiceData & ActorData> | undefined;
  return (d?.name as string) || (d?.label as string) || n.id;
};
const nodeZone = (n: Node): NetworkZone => ((n.data as any)?.zone as NetworkZone) || 'zone-internal';

const DataFlowInner: React.FC = () => {
  const { currentProjectId } = useProject();
  const [projectIds, setProjectIds] = useState<string[] | null>(null);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // OSS-FLOW-2: кастомные (операторские) рёбра из БД per-project.
  const canEdit = hasAccess('serviceConnectEdit');
  const [customEdges, setCustomEdges] = useState<any[]>([]);
  const [newEdgeType, setNewEdgeType] = useState<string>('REST');

  const loadCustomEdges = useCallback(() => {
    if (!currentProjectId) { setCustomEdges([]); return; }
    ApiService.getServiceGraphEdges().then(setCustomEdges).catch(() => setCustomEdges([]));
  }, [currentProjectId]);
  useEffect(() => { loadCustomEdges(); }, [loadCustomEdges]);

  // Per-project: какие каталожные сервисы активны (как в Карте сервисов).
  useEffect(() => {
    if (!currentProjectId) { setProjectIds(null); return; }
    let cancelled = false;
    ApiService.getServiceConnectByProjectId(currentProjectId)
      .then((sc: any) => {
        if (cancelled) return;
        let raw = sc?.data;
        if (raw != null && typeof raw === 'object' && 'data' in raw && !('service_map_ids' in raw)) raw = raw.data;
        let cfg: any = {};
        if (typeof raw === 'string') { try { cfg = JSON.parse(raw); } catch { cfg = {}; } }
        else if (raw && typeof raw === 'object') cfg = raw;
        const ids = Array.isArray(cfg.service_map_ids) ? cfg.service_map_ids : null;
        setProjectIds(ids && ids.length ? ids : null);
      })
      .catch(() => { if (!cancelled) setProjectIds(null); });
    return () => { cancelled = true; };
  }, [currentProjectId]);

  // Базовый набор узлов (сервисы + акторы), отфильтрованный per-project.
  const baseNodes = useMemo(() => {
    let list = ALL_INITIAL_NODES.filter(isServiceOrActor);
    if (projectIds) list = list.filter(n => n.type === 'actor' || projectIds.includes(n.id));
    return list;
  }, [projectIds]);

  const baseIds = useMemo(() => new Set(baseNodes.map(n => n.id)), [baseNodes]);

  // Нормализованные рёбра: курируемые (INITIAL_EDGES) ∪ кастомные (из БД, OSS-FLOW-2),
  // между видимыми узлами. custom=true → редактируемое/удаляемое оператором.
  const allEdges = useMemo(() => {
    const curated = INITIAL_EDGES
      .filter(e => baseIds.has(e.source) && baseIds.has(e.target))
      .map(e => {
        const d = e.data as ConnectorData;
        return { id: e.id, source: e.source, target: e.target, type: d.type as string, label: d.label, isAsync: !!d.isAsync, custom: false };
      });
    const custom = (customEdges || [])
      .filter(e => baseIds.has(e.source) && baseIds.has(e.target))
      .map(e => ({ id: e.id as string, source: e.source as string, target: e.target as string, type: (e.type as string) || 'REST', label: e.label as string, isAsync: !!e.is_async, custom: true }));
    return [...curated, ...custom];
  }, [baseIds, customEdges]);

  // Точки входа: сервисы, в которые приходят рёбра от акторов (entryType=http/external).
  const entryPoints = useMemo(() => {
    const actorIds = new Set(baseNodes.filter(n => n.type === 'actor').map(n => n.id));
    const eps = new Set<string>();
    allEdges.forEach(e => { if (actorIds.has(e.source)) eps.add(e.target); });
    return eps;
  }, [baseNodes, allEdges]);

  // Поиск: оставить совпадающие узлы + их прямых соседей.
  const visibleIds = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return baseIds;
    const matched = new Set<string>();
    baseNodes.forEach(n => { if (nodeName(n).toLowerCase().includes(q)) matched.add(n.id); });
    const withNeighbors = new Set(matched);
    allEdges.forEach(e => {
      if (matched.has(e.source)) withNeighbors.add(e.target);
      if (matched.has(e.target)) withNeighbors.add(e.source);
    });
    return withNeighbors;
  }, [query, baseNodes, allEdges, baseIds]);

  // Раскладка: колонки по зонам, узлы стопкой внутри зоны.
  const rfNodes: FlowNode[] = useMemo(() => {
    const perZoneCount: Record<string, number> = {};
    return baseNodes
      .filter(n => visibleIds.has(n.id))
      .map(n => {
        const zone = nodeZone(n);
        const col = Math.max(0, ZONE_ORDER.indexOf(zone));
        const row = perZoneCount[zone] = (perZoneCount[zone] ?? 0) + 1;
        const group = (n.data as ServiceData | undefined)?.group;
        const color = group ? GROUP_COLORS[group] : (n.type === 'actor' ? '#6A1B9A' : '#607D8B');
        const isEntry = entryPoints.has(n.id);
        const isSel = selectedId === n.id;
        return {
          id: n.id,
          position: { x: X0 + col * COL_W, y: Y0 + (row - 1) * ROW_H },
          data: { label: nodeName(n) },
          sourcePosition: 'right' as const,
          targetPosition: 'left' as const,
          style: {
            background: '#fff',
            color: '#212121',
            border: `2px solid ${isSel ? '#212121' : color}`,
            borderLeft: `6px solid ${color}`,
            borderRadius: 8,
            padding: '6px 10px',
            fontSize: 12,
            width: 210,
            boxShadow: isEntry ? '0 0 0 2px rgba(25,118,210,0.35)' : undefined,
          },
        } as FlowNode;
      });
  }, [baseNodes, visibleIds, entryPoints, selectedId]);

  const rfEdges: Edge[] = useMemo(
    () =>
      allEdges
        .filter(e => visibleIds.has(e.source) && visibleIds.has(e.target))
        .map(e => {
          const st = CONNECTOR_STYLES[e.type as keyof typeof CONNECTOR_STYLES];
          const highlight = selectedId && (e.source === selectedId || e.target === selectedId);
          const color = st?.color || (e.custom ? '#7B1FA2' : '#90A4AE');
          return {
            id: e.id,
            source: e.source,
            target: e.target,
            label: (e.custom ? '✎ ' : '') + (e.label || st?.label || e.type),
            animated: !!st?.animated,
            markerEnd: { type: MarkerType.ArrowClosed, color },
            data: { custom: e.custom },
            style: {
              stroke: color,
              strokeWidth: highlight ? 3 : (st?.strokeWidth || 1.5),
              strokeDasharray: e.custom ? '4 3' : st?.strokeDasharray,
              opacity: selectedId && !highlight ? 0.25 : 1,
            },
            labelStyle: { fontSize: 10, fill: e.custom ? '#7B1FA2' : '#546E7A' },
            labelBgStyle: { fill: '#fff', fillOpacity: 0.8 },
          } as Edge;
        }),
    [allEdges, visibleIds, selectedId]
  );

  const onNodeClick = useCallback((_e: React.MouseEvent, node: Node) => setSelectedId(node.id), []);
  const onPaneClick = useCallback(() => setSelectedId(null), []);

  // OSS-FLOW-2: создать кастомное ребро (drag от узла к узлу). Гейт serviceConnectEdit.
  const onConnect = useCallback(
    (conn: Connection) => {
      if (!canEdit || !conn.source || !conn.target || conn.source === conn.target) return;
      ApiService.createServiceGraphEdge({ source: conn.source, target: conn.target, type: newEdgeType })
        .then(() => loadCustomEdges())
        .catch(e => console.warn('create edge failed', e));
    },
    [canEdit, newEdgeType, loadCustomEdges]
  );

  // OSS-FLOW-2: удалить кастомное ребро по клику (только custom). Гейт serviceConnectEdit.
  const onEdgeClick = useCallback(
    (_e: React.MouseEvent, edge: Edge) => {
      if (!canEdit || !(edge.data as any)?.custom) return;
      if (!window.confirm('Удалить эту связь?')) return;
      ApiService.deleteServiceGraphEdge(edge.id)
        .then(() => loadCustomEdges())
        .catch(err => console.warn('delete edge failed', err));
    },
    [canEdit, loadCustomEdges]
  );

  // Данные инспектора для выбранного узла.
  const inspector = useMemo(() => {
    if (!selectedId) return null;
    const node = baseNodes.find(n => n.id === selectedId);
    if (!node) return null;
    const outgoing = allEdges.filter(e => e.source === selectedId);
    const incoming = allEdges.filter(e => e.target === selectedId);
    return { node, outgoing, incoming };
  }, [selectedId, baseNodes, allEdges]);

  const nameById = useCallback(
    (id: string) => { const n = baseNodes.find(x => x.id === id); return n ? nodeName(n) : id; },
    [baseNodes]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', overflow: 'hidden', background: '#F8F9FA' }}>
      {/* Toolbar */}
      <div style={{ height: 48, background: '#fff', borderBottom: '1px solid #E0E0E0', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Карта потоков данных</span>
        <Badge bg="secondary" style={{ fontSize: 10 }}>{rfNodes.length} узлов · {rfEdges.length} связей</Badge>
        <div style={{ flex: 1 }} />
        {canEdit && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#757575' }} title="Перетащите от узла к узлу, чтобы добавить связь. Клик по своей связи (✎) — удалить.">
            Новая связь:
            <select
              value={newEdgeType}
              onChange={e => setNewEdgeType(e.target.value)}
              style={{ border: '1px solid #E0E0E0', borderRadius: 6, padding: '4px 6px', fontSize: 12, outline: 'none' }}
            >
              {Object.keys(CONNECTOR_STYLES).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </span>
        )}
        <input
          type="text"
          placeholder="Поиск сервиса…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{ border: '1px solid #E0E0E0', borderRadius: 6, padding: '5px 10px', fontSize: 12, width: 220, outline: 'none' }}
        />
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Canvas */}
        <div style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onConnect={onConnect}
            onEdgeClick={onEdgeClick}
            fitView
            fitViewOptions={{ padding: 0.1 }}
            minZoom={0.1}
            maxZoom={2}
            nodesDraggable={false}
            nodesConnectable={canEdit}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={20} size={1} color="#E8E8E8" />
            <Controls showInteractive={false} />
            {/* Легенда зон (колонки) */}
            <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 8, zIndex: 5, flexWrap: 'wrap' }}>
              {ZONE_ORDER.map(z => (
                <span key={z} style={{ fontSize: 10, color: ZONE_COLORS[z].border, background: '#fff', border: `1px solid ${ZONE_COLORS[z].border}`, borderRadius: 4, padding: '1px 6px' }}>
                  {ZONE_LABEL[z]}
                </span>
              ))}
            </div>
          </ReactFlow>
        </div>

        {/* Inspector */}
        <div style={{ width: 300, minWidth: 300, borderLeft: '1px solid #E0E0E0', background: '#fff', overflowY: 'auto', padding: 14, fontSize: 12 }}>
          {!inspector ? (
            <div style={{ color: '#9E9E9E' }}>
              Кликните узел, чтобы увидеть входящие/исходящие потоки данных.
              <div style={{ marginTop: 10, fontSize: 11 }}>
                <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, boxShadow: '0 0 0 2px rgba(25,118,210,0.35)', marginRight: 6 }} />
                — точка входа (приходит запрос от клиента/актора)
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{nodeName(inspector.node)}</div>
              <div style={{ color: '#757575', marginBottom: 10 }}>
                {(inspector.node.data as ServiceData)?.group || (inspector.node.type === 'actor' ? 'Актор' : '')}
                {' · '}{ZONE_LABEL[nodeZone(inspector.node)]}
                {entryPoints.has(inspector.node.id) && <Badge bg="primary" className="ms-2" style={{ fontSize: 9 }}>точка входа</Badge>}
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                Исходящие потоки ({inspector.outgoing.length})
              </div>
              {inspector.outgoing.length === 0 && <div style={{ color: '#BDBDBD', marginBottom: 8 }}>—</div>}
              {inspector.outgoing.map(e => (
                <div key={e.id} style={{ borderLeft: `3px solid ${CONNECTOR_STYLES[e.type as keyof typeof CONNECTOR_STYLES]?.color || '#7B1FA2'}`, paddingLeft: 8, marginBottom: 6 }}>
                  <div style={{ fontWeight: 600 }}>→ {nameById(e.target)}{e.custom && <span style={{ color: '#7B1FA2' }}> ✎</span>}</div>
                  <div style={{ color: '#757575' }}>{e.type}{e.isAsync ? ' · async' : ''}{e.label ? ` · ${e.label}` : ''}</div>
                </div>
              ))}

              <div style={{ fontSize: 11, fontWeight: 700, color: '#757575', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '10px 0 4px' }}>
                Входящие потоки ({inspector.incoming.length})
              </div>
              {inspector.incoming.length === 0 && <div style={{ color: '#BDBDBD' }}>—</div>}
              {inspector.incoming.map(e => (
                <div key={e.id} style={{ borderLeft: `3px solid ${CONNECTOR_STYLES[e.type as keyof typeof CONNECTOR_STYLES]?.color || '#7B1FA2'}`, paddingLeft: 8, marginBottom: 6 }}>
                  <div style={{ fontWeight: 600 }}>← {nameById(e.source)}{e.custom && <span style={{ color: '#7B1FA2' }}> ✎</span>}</div>
                  <div style={{ color: '#757575' }}>{e.type}{e.isAsync ? ' · async' : ''}{e.label ? ` · ${e.label}` : ''}</div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const DataFlowPage: React.FC = () => (
  <ReactFlowProvider>
    <DataFlowInner />
  </ReactFlowProvider>
);

export default DataFlowPage;
